import type { BlockedToolExecution } from "@app/lib/actions/mcp";
import type { GmailSendMailInput } from "@app/lib/api/actions/servers/gmail/types";
import { isGmailSendMailInput } from "@app/lib/api/actions/servers/gmail/types";
import {
  AttachmentChip,
  Button,
  Checkbox,
  GmailLogo,
  Label,
  LinkExternal01,
  Paperclip,
  Send01,
  Trash01,
} from "@dust-tt/sparkle";
import isEqual from "lodash/isEqual";
import type React from "react";
import { useMemo, useState } from "react";

export interface GmailDraftApproval {
  neverAskAgain: boolean;
  editedArguments: Record<string, unknown>;
}

interface GmailDraftReviewCardProps {
  blockedAction?: Pick<
    BlockedToolExecution,
    "actionId" | "inputs" | "metadata"
  >;
  inputs?: GmailSendMailInput | Record<string, unknown>;
  onApprove: (approval: GmailDraftApproval) => Promise<boolean>;
  onReject: () => Promise<boolean>;
  isValidating?: boolean;
  isPulsing?: boolean;
  errorMessage?: string | null;
  alwaysAllowLabel?: string;
  readOnly?: boolean;
}

const fieldClassName =
  "min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed";

function parseAddresses(value: string): string[] {
  return value
    .split(",")
    .map((address) => address.trim())
    .filter((address) => address.length > 0);
}

function autoGrow(element: HTMLTextAreaElement | null) {
  if (element) {
    element.style.height = "auto";
    element.style.height = `${element.scrollHeight}px`;
  }
}

function AddressRow({
  label,
  value,
  placeholder,
  disabled,
  onChange,
  trailing,
}: {
  label: string;
  value: string;
  placeholder?: string;
  disabled: boolean;
  onChange: (value: string) => void;
  trailing?: React.ReactNode;
}) {
  return (
    <label className="flex items-center gap-2 px-4 py-2">
      <span className="w-14 shrink-0 font-medium text-muted-foreground">
        {label}
      </span>
      <input
        type="text"
        className={fieldClassName}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
      {trailing}
    </label>
  );
}

export function GmailDraftReviewCard({
  blockedAction,
  inputs: directInputs,
  onApprove,
  onReject,
  isValidating = false,
  isPulsing = false,
  errorMessage = null,
  alwaysAllowLabel,
  readOnly = false,
}: GmailDraftReviewCardProps) {
  const rawInputs = directInputs ?? blockedAction?.inputs;
  const inputs: GmailSendMailInput | null = useMemo(() => {
    if (rawInputs && isGmailSendMailInput(rawInputs)) {
      return rawInputs;
    }
    if (rawInputs && typeof rawInputs === "object") {
      return rawInputs as GmailSendMailInput;
    }
    return null;
  }, [rawInputs]);

  const original = {
    to: inputs?.to ?? [],
    cc: inputs?.cc ?? [],
    bcc: inputs?.bcc ?? [],
    subject: inputs?.subject ?? "",
    body: inputs?.body ?? "",
  };

  const [neverAskAgain, setNeverAskAgain] = useState(false);
  const [showCcBcc, setShowCcBcc] = useState(
    original.cc.length > 0 || original.bcc.length > 0
  );
  const [to, setTo] = useState(original.to.join(", "));
  const [cc, setCc] = useState(original.cc.join(", "));
  const [bcc, setBcc] = useState(original.bcc.join(", "));
  const [subject, setSubject] = useState(original.subject);
  const [body, setBody] = useState(original.body);

  const replyToMessageId = inputs?.replyToMessageId;
  const isReply = Boolean(replyToMessageId);
  const fromAddress = inputs?.from;
  const attachmentName = inputs?.attachmentFilePath?.split("/").pop() ?? null;
  const gmailHref = replyToMessageId
    ? `https://mail.google.com/mail/u/0/#all/${replyToMessageId}`
    : "https://mail.google.com";
  const alwaysAllowId = `gmail-always-allow-${blockedAction?.actionId ?? "task"}`;

  const isFieldDisabled = isValidating || readOnly;

  const editedArguments = (): Record<string, unknown> => {
    if (readOnly) {
      return {};
    }
    const current: Record<keyof typeof original, unknown> = {
      to: parseAddresses(to),
      cc: parseAddresses(cc),
      bcc: parseAddresses(bcc),
      subject,
      body,
    };
    return Object.fromEntries(
      Object.entries(current).filter(
        ([key, value]) =>
          !isEqual(original[key as keyof typeof original], value)
      )
    );
  };

  return (
    <div className="flex w-full flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-sm">
      <div className="flex items-center justify-between border-b border-border/70 bg-muted-background/60 px-4 py-2.5">
        <div className="flex items-center gap-2">
          <GmailLogo className="h-4 w-4 shrink-0" />
          <span className="text-xs font-semibold text-foreground/90">
            {isReply ? "Email reply draft" : "Email draft"}
          </span>
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span
              className="h-1.5 w-1.5 shrink-0 rounded-full bg-golden-500"
              aria-hidden
            />
            Waiting for you
          </span>
        </div>
        <Button
          size="xs"
          variant="ghost"
          icon={Trash01}
          tooltip="Discard draft"
          disabled={isValidating}
          onClick={() => void onReject()}
        />
      </div>

      <div className="flex flex-col divide-y divide-border/60 text-sm">
        {fromAddress && (
          <div className="flex items-center gap-2 px-4 py-2">
            <span className="w-14 shrink-0 font-medium text-muted-foreground">
              From
            </span>
            <span className="text-foreground">{fromAddress}</span>
          </div>
        )}
        <AddressRow
          label="To"
          value={to}
          placeholder={
            isReply ? "Original thread recipients" : "name@example.com, …"
          }
          disabled={isFieldDisabled}
          onChange={setTo}
          trailing={
            !showCcBcc &&
            !readOnly && (
              <button
                type="button"
                className="shrink-0 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
                onClick={() => setShowCcBcc(true)}
              >
                Cc / Bcc
              </button>
            )
          }
        />
        {showCcBcc && (
          <>
            <AddressRow
              label="Cc"
              value={cc}
              disabled={isFieldDisabled}
              onChange={setCc}
            />
            <AddressRow
              label="Bcc"
              value={bcc}
              disabled={isFieldDisabled}
              onChange={setBcc}
            />
          </>
        )}
        <label className="flex items-center gap-2 px-4 py-2">
          <span className="w-14 shrink-0 font-medium text-muted-foreground">
            Subject
          </span>
          <input
            type="text"
            className={`${fieldClassName} font-semibold`}
            value={subject}
            placeholder={isReply ? "Same as the thread" : "Subject"}
            disabled={isFieldDisabled || isReply}
            onChange={(event) => setSubject(event.target.value)}
          />
        </label>
      </div>

      <textarea
        ref={autoGrow}
        className="max-h-[60vh] min-h-[200px] w-full resize-none overflow-y-auto border-t border-border/60 bg-transparent p-4 font-sans text-sm leading-relaxed text-foreground outline-none placeholder:text-muted-foreground"
        value={body}
        placeholder="Write your message"
        disabled={isFieldDisabled}
        onChange={(event) => {
          setBody(event.target.value);
          autoGrow(event.target);
        }}
      />

      {attachmentName && (
        <div className="border-t border-border/60 px-4 py-2">
          <AttachmentChip label={attachmentName} icon={{ visual: Paperclip }} />
        </div>
      )}

      {errorMessage && (
        <div className="border-t border-warning-200 bg-warning-50 px-4 py-2 text-xs font-medium text-warning-800 dark:border-warning-800 dark:bg-warning-950/40 dark:text-warning-200">
          {errorMessage}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/70 bg-muted-background/60 p-3">
        <div className="flex items-center gap-3">
          <a
            href={gmailHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            <GmailLogo className="h-3 w-3" />
            <span>Open in Gmail</span>
            <LinkExternal01 className="h-2.5 w-2.5 opacity-60" />
          </a>

          {alwaysAllowLabel && (
            <Label
              htmlFor={alwaysAllowId}
              className="flex cursor-pointer flex-row items-center gap-1.5 text-xs text-muted-foreground"
            >
              <Checkbox
                id={alwaysAllowId}
                checked={neverAskAgain}
                disabled={isValidating}
                onCheckedChange={(check) => setNeverAskAgain(Boolean(check))}
              />
              <span className="font-normal">{alwaysAllowLabel}</span>
            </Label>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            label="Discard"
            variant="outline"
            size="xs"
            disabled={isValidating}
            onClick={() => void onReject()}
          />
          <Button
            label={isValidating ? "Sending…" : "Send"}
            variant="highlight"
            size="xs"
            icon={Send01}
            disabled={
              isValidating || (!isReply && parseAddresses(to).length === 0)
            }
            isPulsing={isPulsing}
            onClick={() =>
              void onApprove({
                neverAskAgain,
                editedArguments: editedArguments(),
              })
            }
          />
        </div>
      </div>
    </div>
  );
}
